import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import { apiGet, apiSend, useAdminState, Erro, Toast } from "../../hooks/useAdminApi";

/**
 * HOME (SEÇÕES) — a montagem da home do produto.
 *
 * Uma "seção" é uma FAIXA da home ("Novidades", "Histórias do Antigo Testamento"). Cada
 * faixa tem CAPÍTULOS, que apontam para uma Série OU um Conteúdo, em ordem.
 *
 * ⚠️ Esta é a tela mais perigosa do painel: a ordem daqui É a ordem que a criança vê.
 * Um erro aqui não quebra um formulário — quebra a home do produto.
 *
 * Por isso a tela mostra uma PRÉVIA fiel (paisagem × retrato muda o formato dos cards de
 * verdade) em vez de uma lista abstrata. O admin precisa VER o que está montando.
 *
 * O backend fala em snake_case com prefixo (section_title, chapter_type...). A conversão
 * fica toda aqui — o resto do painel não precisa saber disso.
 */

/**
 * OS FORMATOS DE FAIXA — cada um casa com um carrossel REAL do site.
 *
 * 🔴 A fonte da verdade é `Components/CarouselFactory/CarouselFactory.jsx`, que decide o
 * componente pelo `exhibitionType`. Se um valor não existir lá, o site cai no landscape e
 * o admin acha que escolheu outra coisa. Só coloque aqui o que a factory sabe renderizar.
 *
 * São os 4 que a factory sabe renderizar: landscape · portrait · square · grid.
 */
// Os rótulos usam o termo que o time usa na conversa. "Square" e "Grid" ficam em inglês de
// propósito: é como esses formatos são chamados no brief e nas reuniões — traduzir para
// "Quadrado"/"Grade" faria a pessoa procurar por uma opção que ela não reconhece.
const FORMATOS = [
  ["landscape", "Paisagem", "card deitado, 16:9"],
  ["portrait", "Retrato", "card em pé, 2:3"],
  ["square", "Square", "card 1:1"],
  ["grid", "Grid", "coleção, 2 por linha"],
];

/**
 * A proporção REAL de cada carrossel — medida no CSS do site, não chutada:
 *   landscape → MovieCarousel       24vw × 13.5vw → 16:9   · tira que rola
 *   portrait  → PortraitCarousel    18vw × 27vw   → 2:3    · tira que rola
 *   square    → SquareCarousel      20vw × 20vw   → 1:1    · tira que rola
 *   grid      → CollectionCarousel  flex 0 0 50%  → 16:9   · 2 POR LINHA, quebra linha
 *
 * O `grid` não é só outra proporção: é outro LAYOUT. Os três primeiros são uma tira
 * horizontal que rola; ele empilha 2 por linha. Por isso `colunas` — sem isso a prévia
 * mostraria uma tira e a criança veria uma grade.
 * Se mudar no CSS do site, mude aqui. Prévia que mente é pior que prévia nenhuma.
 */
const FORMATOS_CARD = {
  landscape: { w: 168, h: 95 }, // 16:9
  portrait: { w: 104, h: 156 }, // 2:3
  square: { w: 124, h: 124 }, // 1:1
  grid: { w: 168, h: 95, colunas: 2 }, // 16:9, mas 2 por linha
};

/** A API devolve `chapter_type: 'content' | 'serie'` com o objeto aninhado correspondente. */
function lerCapitulo(c) {
  const s = c.chapter_serie;
  const ct = c.chapter_content;
  if (s)
    return {
      tipo: "serie",
      id: s.serie_id,
      titulo: s.serie_title,
      thumb: s.serie_thumbnail_url,
      index: c.chapter_index,
    };
  if (ct)
    return {
      tipo: "content",
      id: ct.content_id,
      titulo: ct.content_title,
      thumb: ct.content_thumbnail_url,
      index: c.chapter_index,
    };
  return null;
}

const paraApi = (secao) => ({
  section_title: secao.titulo,
  section_keywords: secao.keywords,
  section_exhibition_type: secao.formato,
  section_index: secao.index,
  section_chapters: secao.itens.map((i, k) => ({
    type: i.tipo,
    ...(i.tipo === "serie" ? { serie_id: i.id } : { content_id: i.id }),
    index: k + 1,
  })),
});

export default function Home() {
  const { tema, setTema } = useOutletContext();
  const { erro, toast, rodar, limparErro } = useAdminState();

  const [secoes, setSecoes] = useState([]);
  const [acervo, setAcervo] = useState({ series: [], contents: [] });
  const [seletor, setSeletor] = useState(null); // { secaoId }
  const [editando, setEditando] = useState(null); // seção ou {} para nova
  const [confirmar, setConfirmar] = useState(null);

  const carregar = useCallback(
    () =>
      rodar(async () => {
        const [s, se, co] = await Promise.all([
          apiGet(API_ROUTES.SECTIONS.list()),
          apiGet(API_ROUTES.SERIES.list()),
          apiGet(API_ROUTES.CONTENTS.list()),
        ]);
        setSecoes(
          (s.sections || [])
            .sort((a, b) => a.section_index - b.section_index)
            .map((x) => ({
              id: x.section_id,
              titulo: x.section_title,
              keywords: x.section_keywords || [],
              formato: x.section_exhibition_type || "landscape",
              index: x.section_index,
              itens: (x.section_chapters || [])
                .map(lerCapitulo)
                .filter(Boolean)
                .sort((a, b) => a.index - b.index),
            }))
        );
        setAcervo({
          series: se.series || se || [],
          contents: co.contents || co || [],
        });
      }),
    [rodar]
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  const salvarSecao = (secao, msg) =>
    rodar(async () => {
      await apiSend(API_ROUTES.SECTIONS.update(secao.id), "PUT", paraApi(secao));
      await carregar();
    }, msg);

  /**
   * Reordenar as FAIXAS. O backend tem um endpoint dedicado (`/sections/sort`) que recebe
   * todas de uma vez — mandar uma a uma deixaria a home embaralhada no meio do caminho.
   */
  const moverSecao = (i, d) => {
    const nova = [...secoes];
    const j = i + d;
    if (j < 0 || j >= nova.length) return;
    [nova[i], nova[j]] = [nova[j], nova[i]];
    rodar(async () => {
      await apiSend(API_ROUTES.SECTIONS.sort(), "PUT", {
        sections: nova.map((s, k) => ({ section_id: s.id, section_index: k + 1 })),
      });
      await carregar();
    }, "Ordem da home atualizada.");
  };

  /** Mover um item DENTRO da faixa (a ordem dos cards na tira). */
  const moverItem = (secao, i, d) => {
    const itens = [...secao.itens];
    const j = i + d;
    if (j < 0 || j >= itens.length) return;
    [itens[i], itens[j]] = [itens[j], itens[i]];
    salvarSecao({ ...secao, itens });
  };

  const removerItem = (secao, i) =>
    salvarSecao(
      { ...secao, itens: secao.itens.filter((_, k) => k !== i) },
      "Item removido da faixa."
    );

  const adicionarItem = (secao, item) => {
    if (secao.itens.some((x) => x.id === item.id && x.tipo === item.tipo)) {
      setSeletor(null);
      return;
    }
    salvarSecao({ ...secao, itens: [...secao.itens, item] }, "Item adicionado.");
    setSeletor(null);
  };

  const trocarFormato = (secao, formato) =>
    salvarSecao({ ...secao, formato }, "Formato da faixa alterado.");

  return (
    <>
      <AdminHeader
        tema={tema}
        setTema={setTema}
        titulo="Home (Seções)"
        subtitulo="A montagem da home do app. A ordem aqui é a ordem que a criança vê."
        acoes={
          <button
            className="adm__btn"
            onClick={() =>
              setEditando({ titulo: "", keywords: [], formato: "landscape", itens: [] })
            }
          >
            + Nova seção
          </button>
        }
      />

      <div className="adm__body">
        <Erro erro={erro} onFechar={limparErro} />

        <p style={{ fontSize: 12.5, color: "var(--text3)", margin: "0 0 14px" }}>
          Prévia da ordem real da home
        </p>

        {secoes.length === 0 && (
          <div className="adm__card">
            <p className="adm__vazio">
              Nenhuma seção ainda. A home do app está vazia.
              <br />
              <span style={{ fontSize: 12.5 }}>Crie a primeira faixa para começar.</span>
            </p>
          </div>
        )}

        {secoes.map((s, i) => (
          <Faixa
            key={s.id}
            secao={s}
            primeira={i === 0}
            ultima={i === secoes.length - 1}
            onSubir={() => moverSecao(i, -1)}
            onDescer={() => moverSecao(i, 1)}
            onFormato={(f) => trocarFormato(s, f)}
            onMoverItem={(k, d) => moverItem(s, k, d)}
            onRemoverItem={(k) => removerItem(s, k)}
            onAdicionar={() => setSeletor({ secao: s })}
            onEditar={() => setEditando(s)}
            onExcluir={() =>
              setConfirmar({
                perigo: true,
                titulo: "Excluir seção",
                texto: `A faixa “${s.titulo}” some da home. Os ${s.itens.length} ${
                  s.itens.length === 1 ? "item continua" : "itens continuam"
                } no acervo — só a faixa é removida.`,
                onOk: () =>
                  rodar(async () => {
                    await apiSend(API_ROUTES.SECTIONS.delete(s.id), "DELETE");
                    setConfirmar(null);
                    await carregar();
                  }, "Seção excluída."),
              })
            }
          />
        ))}
      </div>

      {seletor && (
        <Seletor
          acervo={acervo}
          jaNaFaixa={seletor.secao.itens}
          onFechar={() => setSeletor(null)}
          onEscolher={(item) => adicionarItem(seletor.secao, item)}
        />
      )}

      {editando && (
        <FormSecao
          secao={editando}
          onFechar={() => setEditando(null)}
          onSalvar={(d) =>
            rodar(async () => {
              if (d.id) await apiSend(API_ROUTES.SECTIONS.update(d.id), "PUT", paraApi(d));
              else
                await apiSend(API_ROUTES.SECTIONS.create(), "POST", {
                  ...paraApi(d),
                  section_index: secoes.length + 1,
                });
              setEditando(null);
              await carregar();
            }, d.id ? "Seção atualizada." : "Seção criada.")
          }
        />
      )}

      {confirmar && <Confirmar {...confirmar} onCancelar={() => setConfirmar(null)} />}

      <Toast toast={toast} />
    </>
  );
}

/** Uma faixa da home, com os cards no formato REAL (é a prévia que faz a tela valer). */
function Faixa({
  secao,
  primeira,
  ultima,
  onSubir,
  onDescer,
  onFormato,
  onMoverItem,
  onRemoverItem,
  onAdicionar,
  onEditar,
  onExcluir,
}) {
  // A PREVIEW NÃO PODE MENTIR: estas proporções são as REAIS de cada carrossel do site.
  // Se mudarem lá, mudam aqui — senão o admin escolhe uma coisa e a criança vê outra.
  //   landscape → MovieCarousel     24vw × 13.5vw → 16:9
  //   portrait  → PortraitCarousel  18vw × 27vw   → 2:3
  //   square    → SquareCarousel    20vw × 20vw   → 1:1
  // (Antes isto era um booleano `retrato`, que só sabia da existência de 2 formatos.)
  const { w: cardW, h: cardH, colunas } = FORMATOS_CARD[secao.formato] || FORMATOS_CARD.landscape;

  return (
    <div className="adm__card" style={{ padding: 16, marginBottom: 14, display: "flex", gap: 12 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, paddingTop: 2 }}>
        <span style={{ color: "var(--text3)", fontSize: 13, cursor: "grab" }} title="Ordem na home">⠿</span>
        <button className="adm__btnGhost" style={{ padding: "2px 6px", fontSize: 9 }} disabled={primeira} onClick={onSubir}>▲</button>
        <button className="adm__btnGhost" style={{ padding: "2px 6px", fontSize: 9 }} disabled={ultima} onClick={onDescer}>▼</button>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <strong style={{ fontSize: 15 }}>{secao.titulo}</strong>

          {/* O formato muda os cards DE VERDADE — não é um rótulo. Cada opção aqui casa
              com um carrossel real da CarouselFactory do site. */}
          <div style={{ display: "flex", gap: 3, background: "var(--elev)", borderRadius: 8, padding: 3 }}>
            {FORMATOS.map(([k, l]) => (
              <button
                key={k}
                onClick={() => onFormato(k)}
                style={{
                  border: "none",
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontSize: 11.5,
                  fontWeight: secao.formato === k ? 600 : 500,
                  cursor: "pointer",
                  background: secao.formato === k ? "var(--red)" : "transparent",
                  color: secao.formato === k ? "#fff" : "var(--text2)",
                  font: "inherit",
                  fontFamily: "inherit",
                }}
              >
                {l}
              </button>
            ))}
          </div>

          {secao.keywords.map((k) => (
            <span key={k} className="adm__chip">{k}</span>
          ))}

          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <button className="adm__btnGhost" onClick={onEditar}>Editar</button>
            <button
              className="adm__btnGhost"
              onClick={onExcluir}
              style={{ color: "var(--cancel)", borderColor: "rgba(255,90,95,.3)" }}
            >
              Excluir
            </button>
          </div>
        </div>

        {/* A PRÉVIA. Grid é outro layout, não outra proporção: os demais são uma tira que
            rola; ele empilha 2 por linha. Mostrar tira aqui seria mentir. */}
        <div
          style={
            colunas
              ? {
                  display: "grid",
                  gridTemplateColumns: `repeat(${colunas}, ${cardW}px)`,
                  gap: 10,
                  paddingBottom: 4,
                }
              : { display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }
          }
        >
          {secao.itens.map((it, k) => (
            <div key={`${it.tipo}-${it.id}`} style={{ flexShrink: 0, width: cardW }}>
              <div
                style={{
                  width: cardW,
                  height: cardH,
                  borderRadius: 8,
                  background: it.thumb ? `center/cover url("${it.thumb}")` : "var(--elev)",
                  border: "1px solid var(--border)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <span
                  className="adm__chip"
                  style={{ position: "absolute", left: 5, bottom: 5, fontSize: 9, background: "rgba(0,0,0,.66)", color: "#fff", border: "none" }}
                >
                  {it.tipo === "serie" ? "SÉRIE" : "CONTEÚDO"}
                </span>

                <button
                  onClick={() => onRemoverItem(k)}
                  title="Tirar da faixa"
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    border: "none",
                    background: "rgba(0,0,0,.72)",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: 11,
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 5 }}>
                <button className="adm__btnGhost" style={{ padding: "1px 5px", fontSize: 9 }} disabled={k === 0} onClick={() => onMoverItem(k, -1)}>◀</button>
                <span
                  style={{
                    fontSize: 11.5,
                    color: "var(--text2)",
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={it.titulo}
                >
                  {it.titulo}
                </span>
                <button className="adm__btnGhost" style={{ padding: "1px 5px", fontSize: 9 }} disabled={k === secao.itens.length - 1} onClick={() => onMoverItem(k, 1)}>▶</button>
              </div>
            </div>
          ))}

          <button
            onClick={onAdicionar}
            style={{
              flexShrink: 0,
              width: cardW,
              height: cardH,
              borderRadius: 8,
              border: "1px dashed var(--border)",
              background: "transparent",
              color: "var(--text3)",
              cursor: "pointer",
              fontSize: 12,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              font: "inherit",
            }}
          >
            <span style={{ fontSize: 17 }}>+</span>
            Adicionar item
          </button>
        </div>
      </div>
    </div>
  );
}

/** Escolher uma Série ou um Conteúdo para pôr na faixa. */
function Seletor({ acervo, jaNaFaixa, onFechar, onEscolher }) {
  const [busca, setBusca] = useState("");
  const [tipo, setTipo] = useState("serie");

  const lista = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const itens =
      tipo === "serie"
        ? acervo.series.map((s) => ({
            tipo: "serie",
            id: s.serie_id || s.id,
            titulo: s.serie_title || s.title,
            thumb: s.serie_thumbnail_url || s.thumbnailUrl,
          }))
        : acervo.contents.map((c) => ({
            tipo: "content",
            id: c.content_id || c.id,
            titulo: c.content_title || c.title,
            thumb: c.content_thumbnail_url || c.thumbnailUrl,
          }));
    return itens
      .filter((i) => i.id && (!q || String(i.titulo).toLowerCase().includes(q)))
      .slice(0, 60);
  }, [acervo, tipo, busca]);

  const jaTem = (i) => jaNaFaixa.some((x) => x.id === i.id && x.tipo === i.tipo);

  return (
    <div className="adm__modalFundo" onClick={onFechar}>
      <div className="adm__modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(560px,100%)" }}>
        <h3>Adicionar à faixa</h3>
        <p className="adm__modalHint">
          Uma faixa pode misturar séries e conteúdos avulsos.
        </p>

        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {[["serie", "Séries"], ["content", "Conteúdos"]].map(([k, l]) => (
            <button key={k} onClick={() => setTipo(k)} className={tipo === k ? "adm__btn" : "adm__btnGhost"}>
              {l}
            </button>
          ))}
          <input
            className="adm__input"
            placeholder="Buscar…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ flex: 1 }}
          />
        </div>

        <div style={{ maxHeight: 320, overflowY: "auto" }}>
          {lista.map((i) => {
            const dentro = jaTem(i);
            return (
              <button
                key={`${i.tipo}-${i.id}`}
                onClick={() => !dentro && onEscolher(i)}
                disabled={dentro}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 11,
                  width: "100%",
                  background: "none",
                  border: "none",
                  borderBottom: "1px solid var(--line)",
                  padding: "9px 4px",
                  cursor: dentro ? "default" : "pointer",
                  opacity: dentro ? 0.4 : 1,
                  textAlign: "left",
                  font: "inherit",
                  color: "var(--text)",
                }}
              >
                <span
                  style={{
                    width: 46,
                    height: 28,
                    borderRadius: 5,
                    flexShrink: 0,
                    background: i.thumb ? `center/cover url("${i.thumb}")` : "var(--elev)",
                    border: "1px solid var(--border)",
                  }}
                />
                <span style={{ flex: 1, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {i.titulo}
                </span>
                {dentro && <span className="adm__chip">já está na faixa</span>}
              </button>
            );
          })}
          {lista.length === 0 && <p className="adm__vazio">Nada encontrado.</p>}
        </div>

        <div className="adm__modalAcoes">
          <button className="adm__btnGhost" onClick={onFechar}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

function FormSecao({ secao, onFechar, onSalvar }) {
  const [d, setD] = useState(secao);
  const [kw, setKw] = useState("");

  const addKw = () => {
    const v = kw.trim();
    if (!v || d.keywords.includes(v)) return;
    setD({ ...d, keywords: [...d.keywords, v] });
    setKw("");
  };

  return (
    <div className="adm__modalFundo" onClick={onFechar}>
      <div className="adm__modal" onClick={(e) => e.stopPropagation()}>
        <h3>{d.id ? "Editar seção" : "Nova seção"}</h3>
        <p className="adm__modalHint">
          A seção é uma <strong>faixa da home</strong>. Depois de criar, você adiciona os itens
          direto na faixa.
        </p>

        <div className="adm__campo">
          <label>Título</label>
          <input
            className="adm__input"
            value={d.titulo}
            onChange={(e) => setD({ ...d, titulo: e.target.value })}
            placeholder="ex.: Histórias do Antigo Testamento"
          />
        </div>

        <div className="adm__campo">
          <label>Formato dos cards</label>
          <select
            className="adm__select"
            value={d.formato}
            onChange={(e) => setD({ ...d, formato: e.target.value })}
          >
            {/* mesma fonte do toggle da faixa — não duplique a lista aqui */}
            {FORMATOS.map(([k, l, desc]) => (
              <option key={k} value={k}>
                {l} ({desc})
              </option>
            ))}
          </select>
        </div>

        <div className="adm__campo">
          <label>Palavras-chave</label>
          <div style={{ display: "flex", gap: 7 }}>
            <input
              className="adm__input"
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addKw())}
              placeholder="digite e tecle Enter"
            />
            <button className="adm__btnGhost" onClick={addKw}>Adicionar</button>
          </div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 8 }}>
            {d.keywords.map((k) => (
              <button
                key={k}
                className="adm__chip"
                onClick={() => setD({ ...d, keywords: d.keywords.filter((x) => x !== k) })}
                style={{ cursor: "pointer" }}
                title="Remover"
              >
                {k} ✕
              </button>
            ))}
          </div>
        </div>

        <div className="adm__modalAcoes">
          <button className="adm__btnGhost" onClick={onFechar}>Cancelar</button>
          <button className="adm__btn" disabled={!d.titulo?.trim()} onClick={() => onSalvar(d)}>
            {d.id ? "Salvar" : "Criar seção"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Confirmar({ titulo, texto, perigo, onOk, onCancelar }) {
  return (
    <div className="adm__modalFundo" onClick={onCancelar} style={{ zIndex: 200 }}>
      <div className="adm__modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(420px,100%)" }}>
        <h3 style={{ color: perigo ? "var(--cancel)" : undefined }}>{titulo}</h3>
        <p className="adm__modalHint">{texto}</p>
        <div className="adm__modalAcoes">
          <button className="adm__btnGhost" onClick={onCancelar}>Cancelar</button>
          <button className={`adm__btn ${perigo ? "adm__btnDanger" : ""}`} onClick={onOk}>
            {titulo}
          </button>
        </div>
      </div>
    </div>
  );
}
